import { useMemo, useState } from 'react';
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { ArrowDownward, ArrowUpward, Close, LockOutlined } from '@mui/icons-material';
import { SelectField } from '../../components/fields/SelectField';
import type { Catalog, LayoutWidget, RangeKey } from './dashboardApi';
import { RANGE_LABELS, SPAN_OPTIONS } from './dashboardApi';

interface Props {
  catalog: Catalog;
  value: LayoutWidget[];
  onChange: (next: LayoutWidget[]) => void;
  /** Titles of widgets already on the layout that the catalog (this user's own eligible set) does not list. */
  lockedTitles?: Record<string, string>;
}

// Reorder uses buttons rather than drag-and-drop: no new dependency, and every action works from the keyboard.
export function LayoutEditor({ catalog, value, onChange, lockedTitles = {} }: Props) {
  const byId = useMemo(() => new Map(catalog.widgets.map((w) => [w.id, w])), [catalog]);
  const [toAdd, setToAdd] = useState('');
  const available = catalog.widgets.filter((w) => !value.some((v) => v.id === w.id));
  const categories = Array.from(new Set(available.map((w) => w.category)));

  const patch = (i: number, p: Partial<LayoutWidget>) => onChange(value.map((w, j) => (j === i ? { ...w, ...p } : w)));
  const setFilter = (i: number, k: 'range' | 'branchId', v: string) => {
    const filters = { ...value[i].filters, [k]: v || undefined };
    if (!filters.range) delete filters.range;
    if (!filters.branchId) delete filters.branchId;
    patch(i, { filters: Object.keys(filters).length ? filters : undefined });
  };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => {
    const def = byId.get(toAdd);
    if (!def) return;
    onChange([...value, { id: def.id, span: def.defaultSpan }]);
    setToAdd('');
  };

  return (
    <Stack spacing={1.5}>
      {value.length === 0 && <Typography color="text.secondary">No widgets yet - add one below.</Typography>}
      {value.map((w, i) => {
        const def = byId.get(w.id);
        return (
          <Paper key={w.id} variant="outlined" sx={{ p: 1.5 }} data-testid={`layout-row-${w.id}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: def ? 1 : 0 }}>
              <Typography fontWeight={700} sx={{ flex: 1 }}>
                {def?.title ?? lockedTitles[w.id] ?? w.id}
                {!def && (
                  <Tooltip title="You cannot view this widget yourself, so you cannot change it - you can still move or remove it."><LockOutlined fontSize="inherit" sx={{ ml: 0.75, verticalAlign: 'middle' }} /></Tooltip>
                )}
              </Typography>
              <IconButton size="small" aria-label={`Move ${def?.title ?? w.id} up`} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpward fontSize="small" /></IconButton>
              <IconButton size="small" aria-label={`Move ${def?.title ?? w.id} down`} disabled={i === value.length - 1} onClick={() => move(i, 1)}><ArrowDownward fontSize="small" /></IconButton>
              <IconButton size="small" aria-label={`Remove ${def?.title ?? w.id}`} onClick={() => onChange(value.filter((_, j) => j !== i))}><Close fontSize="small" /></IconButton>
            </Box>
            {def && (
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
                <SelectField size="small" label="Width" value={w.span} onChange={(e) => patch(i, { span: Number(e.target.value) })} options={SPAN_OPTIONS} />
                {def.ranges.length > 0 && (
                  <SelectField size="small" label="Period" value={w.filters?.range ?? ''} onChange={(e) => setFilter(i, 'range', e.target.value)}
                    emptyLabel={`Default (${def.defaultRange ? RANGE_LABELS[def.defaultRange] : 'n/a'})`}
                    options={def.ranges.map((r: RangeKey) => ({ value: r, label: RANGE_LABELS[r] }))} />
                )}
                {def.branchFilter && (
                  <SelectField size="small" label="Branch" value={w.filters?.branchId ?? ''} onChange={(e) => setFilter(i, 'branchId', e.target.value)}
                    emptyLabel="All my branches" options={catalog.branches.map((b) => ({ value: b.id, label: b.name }))} />
                )}
              </Box>
            )}
          </Paper>
        );
      })}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <SelectField size="small" label="Add a widget" value={toAdd} onChange={(e) => setToAdd(e.target.value)} emptyLabel={available.length ? 'Choose a widget…' : 'Every available widget is already on the layout'} disabled={available.length === 0}>
          {categories.map((c) => (
            <optgroup key={c} label={c.charAt(0) + c.slice(1).toLowerCase()}>
              {available.filter((w) => w.category === c).map((w) => <option key={w.id} value={w.id}>{w.title} - {w.description}</option>)}
            </optgroup>
          ))}
        </SelectField>
        <Button variant="outlined" onClick={add} disabled={!toAdd} sx={{ whiteSpace: 'nowrap', mt: '1px', height: 40 }}>Add</Button>
      </Box>
    </Stack>
  );
}
